import { useEffect, useState } from 'react'
import {
    Box,
    Button,
    Container,
    Divider,
    List,
    ListItem,
    Stack,
} from '@mui/material'
import {
    createSearchParams,
    useNavigate, useNavigationType,
    useSearchParams,
} from 'react-router-dom'
import LocationListItem from '../../../components/LocationListItem'
import {
    INNSYN_SEARCH_NAME_PATH,
    INNSYN_SEARCH_OEREMERKE_OMRAADE_PATH,
    INNSYN_SEARCH_OEREMERKE_PATH,
} from '../../../paths'
import { useInnsynContext } from '../../../context/InnsynContextProvider'
import EarTagListItem from '../../../components/EarTagListItem'
import { FormattedMessage } from 'react-intl'
import NavigationHeader from '../../../components/NavigationHeader'
import loading from '../../../assets/loadingplain.gif'

export const SearchResults = () => {
    const [query] = useSearchParams()
    const newResultsPerLoad = 25
    const [maxListeStr, setMaxListeStr] = useState(() => {
        const saved = sessionStorage.getItem('maxListeStr')
        return saved ? parseInt(saved) : newResultsPerLoad
    })
    const areaStringArray = query.getAll('a')
    const districtStringArray = query.getAll('d')
    const siidasStringArray = query.getAll('s')
    const {
        merker,
        geografi,
        snitt,
        fetchPersonerMedMerke,
        fetchPersonerMedNavn,
        brukerInformasjon,
        isLoadingMerke,
    } = useInnsynContext()
    const [personer, setPerson] = useState<Person[]>()
    const [erNavnesoek] = useState(
        query.get('fornavn') !== null || query.get('etternavn') !== null
    )
    const [isLoadingNavneSoek, setIsLoadingNavneSoek] = useState(false)
    const navigate = useNavigate()
    const navigationType = useNavigationType()
    const [merkerInResultList, setMerkerInResultList] = useState<
        Merke[] | undefined
    >(undefined)

    const pos1 = snitt?.filter(
        (cut) =>
            cut.id.toString() === query.get('pos_1') &&
            cut.posisjon.toString() === '1'
    )[0]
    const pos2 = snitt?.filter(
        (cut) =>
            cut.id.toString() === query.get('pos_2') &&
            cut.posisjon.toString() === '2'
    )[0]
    const pos3 = snitt?.filter(
        (cut) =>
            cut.id.toString() === query.get('pos_3') &&
            cut.posisjon.toString() === '3'
    )[0]
    const pos4 = snitt?.filter(
        (cut) =>
            cut.id.toString() === query.get('pos_4') &&
            cut.posisjon.toString() === '4'
    )[0]
    const pos5 = snitt?.filter(
        (cut) =>
            cut.id.toString() === query.get('pos_5') &&
            cut.posisjon.toString() === '5'
    )[0]
    const pos6 = snitt?.filter(
        (cut) =>
            cut.id.toString() === query.get('pos_6') &&
            cut.posisjon.toString() === '6'
    )[0]

    const valgteSnitt = [pos1, pos2, pos3, pos4, pos5, pos6].filter(
        (pos) => !!pos
    )

    const updateRegex = () => {
        let reg = ''
        if (valgteSnitt) {
            let tempbokstavmoenster = ''
            valgteSnitt.forEach((snitt) => {
                if (snitt) {
                    tempbokstavmoenster = tempbokstavmoenster.concat(
                        snitt.bokstavkombinasjon
                    )
                }
            })
            for (let i = 0; i < tempbokstavmoenster.length; i++) {
                if (i === 0) {
                    reg = reg.concat('.*')
                }
                reg = reg.concat(`(?=.*${tempbokstavmoenster[i]})`)
            }
            reg = reg.concat('.*')
            return new RegExp(reg)
        }
    }
    const getPersoner = async () => {
        const personRes = await fetchPersonerMedNavn(query)
        setPerson(personRes)
        return personRes
    }

    const getPersonerForMerker = async (merkerListe: Merke[] | undefined) => {
        setPerson([])
        if (
            merkerListe !== undefined &&
            merkerListe.length <= 10 &&
            merkerListe.length > 0 &&
            brukerInformasjon?.harTilgang
        ) {
            const sakIdList = merkerListe.map((merke) => merke.id.toString())
            const personRes = await fetchPersonerMedMerke(sakIdList)
            setPerson(personRes)
        }
    }

    const merkerInAreaOrName = (personResponse?: Person[]) => {
        if (erNavnesoek && personResponse !== undefined) {
            return merker?.filter((m) => {
                return personResponse.some((p) => {
                    return p.id === m.id
                })
            })
        }
        const merkerInSiida = merker?.filter((merke) =>
            merke.siidaId && siidasStringArray.includes(merke.siidaId?.toString())
        )

        if (areaStringArray.length === 0 && siidasStringArray.length === 0) {
            return merker
        }
        const merkerInArea = merker?.filter((merke) =>
            areaStringArray.includes(merke.omraadeId?.toString())
        )

        if (districtStringArray.length === 0) {
            return merkerInArea?.concat(merkerInSiida ?? [])
        }

        const omraader = geografi?.filter((omrrade) =>
            areaStringArray.includes(omrrade.omraadeId.toString())
        )

        const map = new Map()
        omraader?.forEach((el) =>
            map.set(
                el.omraadeId,
                districtStringArray.filter((it) =>
                    el.distrikter.some(
                        (dist) => dist.distriktId.toString() === it
                    )
                )
            )
        )

        const merkerInDistricts = merkerInArea?.filter((merke) => {
            const districtsInResults = map.get(merke.omraadeId)
            return districtsInResults.length === 0
                ? true
                : districtsInResults.includes(merke.distriktId?.toString())
        })

        return merkerInDistricts?.concat(merkerInSiida ?? [])
    }

    const merkerInResults = (merkerFoerSnittSortering: Merke[] | undefined) => {
        if (valgteSnitt.length === 0) {
            return merkerFoerSnittSortering
        } else {
            return merkerFoerSnittSortering?.filter((m) => {
                return valgteSnitt.every((s) => {
                    if (!s) return false
                    const key = `snittPosisjon${s.posisjon}` as keyof Merke
                    return m[key]?.toString().includes(s?.bokstavkombinasjon)
                })
            })
        }
    }

    const merkerInExtendedResults = (
        merker: Merke[] | undefined,
        updateRegex: RegExp | undefined
    ) => {
        if (valgteSnitt.length === 0) {
            return merker
        } else {
            return merker?.filter((m) => {
                let bokstavmoensterMerke = ''
                for (let i = 0; i < 6; i++) {
                    const pos_key = `snittPosisjon${i + 1}` as keyof Merke
                    if (m && m[pos_key] && m[pos_key] !== null) {
                        const key = m[pos_key] as string
                        bokstavmoensterMerke = bokstavmoensterMerke.concat(key)
                    }
                }
                if (updateRegex !== undefined)
                    return bokstavmoensterMerke?.match(updateRegex) !== null
            })
        }
    }

    const scrollCheck = (e: any) => {
        if (
            window.innerHeight + e.target.documentElement.scrollTop + 1 >=
            e.target.documentElement.scrollHeight
        ) {
            if (merker && maxListeStr > merker.length) {
                setMaxListeStr(merker.length)
            } else if (merker && maxListeStr < merker.length) {
                setMaxListeStr(maxListeStr + newResultsPerLoad)
            }
        }
    }

    useEffect(() => {
        if (erNavnesoek) {
            setIsLoadingNavneSoek(true)
            getPersoner().then((pers) => {
                setMerkerInResultList(merkerInResults(merkerInAreaOrName(pers)))
                setIsLoadingNavneSoek(false)
            })
        } else if (!erNavnesoek) {
            setMerkerInResultList(merkerInResults(merkerInAreaOrName()))
            getPersonerForMerker(merkerInResults(merkerInAreaOrName()))
        }
    }, [merker])

    useEffect(() => {
        window.addEventListener('scroll', scrollCheck)
        sessionStorage.setItem('maxListeStr', maxListeStr.toString())
        return () => window.removeEventListener('scroll', scrollCheck)
    }, [maxListeStr])

    useEffect(() => {
        const savedScroll = sessionStorage.getItem('scrollPosition')
        if (savedScroll) {
            requestAnimationFrame(() => {
                window.scrollTo(0, parseInt(savedScroll))
                sessionStorage.removeItem('scrollPosition')
            })
        }
    }, [navigationType])

    const findPersonNameForMerkesak = (merke: Merke) => {
        const person = personer?.find((pers) => merke.id === pers.id)
        if (person?.organisasjon?.orgnr !== null) {
            if (!person?.organisasjon.orgnavn || !person?.organisasjon.orgnr)
                return ''
            return `${person?.organisasjon.orgnavn} ${person?.organisasjon.orgnr}`
        } else {
            if (!person?.personnavn.fornavn || !person.personnavn.etternavn)
                return ''
            return `${person?.personnavn.fornavn} ${person?.personnavn.etternavn}`
        }
    }

    const expandSearch = () => {
        window.scrollTo(0, 0)
        setMerkerInResultList(
            merkerInExtendedResults(merkerInAreaOrName(), updateRegex())
        )
        getPersonerForMerker(
            merkerInExtendedResults(merkerInAreaOrName(), updateRegex())
        )
    }
    return (
        <Container sx={{ height: '100%' }}>
            <Stack>
                <NavigationHeader
                    rightText='button-start-page'
                    onClickRightText={() => {
                        sessionStorage.removeItem('maxListeStr')
                        navigate('/app/search')}
                }
                    onClickBack={() => {
                        sessionStorage.removeItem('maxListeStr')
                        if (query.get('search_type') === 'name') {
                            query.delete('search_type')
                            navigate({
                                pathname: INNSYN_SEARCH_NAME_PATH,
                            })
                        } else if (query.get('search_type') === 'cut') {
                            query.delete('search_type')
                            navigate({
                                pathname: INNSYN_SEARCH_OEREMERKE_PATH,
                                search: `?${createSearchParams(query)}`,
                            })
                        }
                    }}
                />
                {!erNavnesoek && (
                    <Stack sx={{ marginTop: '0.5rem' }}>
                        <LocationListItem
                            pathname={INNSYN_SEARCH_OEREMERKE_OMRAADE_PATH}
                        />
                    </Stack>
                )}
                <Box sx={{ fontSize: '1.25rem', marginTop: '1rem' }}>
                    <FormattedMessage
                        id={'results.title'}
                        values={{ count: merkerInResultList?.length }}
                    />
                </Box>
                <Box
                    sx={{
                        fontSize: '0.8rem',
                        marginTop: '0.5rem',
                        fontStyle: 'italic',
                    }}>
                    {brukerInformasjon?.harTilgang && (
                        <FormattedMessage id={'results.name-tip'} />
                    )}
                </Box>
                <List>
                    {merkerInResultList
                        ?.sort((m1, m2) => {
                            if (m1.kapittel < m2.kapittel) return -1
                            else if (m1.kapittel > m2.kapittel) return 1
                            else {
                                if (m1.merkenr < m2.merkenr) return -1
                                else if (m1.merkenr > m2.merkenr) return 1
                                else return 0
                            }
                        })
                        .slice(0, maxListeStr)
                        .map((merke, index) => (
                            <div key={`result-list-${index}`}>
                                <ListItem
                                    sx={{
                                        justifyContent: 'center',
                                        paddingRight: 0,
                                    }}>
                                    <EarTagListItem
                                        id={merke.id}
                                        navn={findPersonNameForMerkesak(merke)}
                                    />
                                </ListItem>
                                <Divider />
                            </div>
                        ))}
                </List>
                {isLoadingMerke || isLoadingNavneSoek ? (
                    <div style={{ marginTop: '3rem', alignSelf: 'center' }}>
                        <img
                            style={{ width: '9rem', color: 'blue' }}
                            src={loading}
                            alt='loading.gif'
                        />
                    </div>
                ) : (
                    valgteSnitt.length > 0 && (
                        <Stack
                            sx={{
                                alignItems: 'center',
                                marginTop: '2rem',
                                color: 'primary.main',
                            }}>
                            <FormattedMessage id='result-page.expand-search-text' />
                            <Button
                                onClick={() => expandSearch()}
                                variant='outlined'
                                sx={{
                                    marginBottom: '3rem',
                                    marginTop: '1rem',
                                    maxWidth: '25rem',
                                    fontWeight: '400',
                                }}>
                                <FormattedMessage id='result-page.expand-search' />
                            </Button>
                        </Stack>
                    )
                )}
            </Stack>
        </Container>
    )
}

export default SearchResults
